import type { BreadcrumbProps } from 'antd';
import { Breadcrumb } from 'antd';
import { forwardRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

export interface AppBreadcrumbProps extends BreadcrumbProps {
  paramsLabel?: string;
  showHome?: boolean;
  showContainer?: boolean;
}

const AppBreadcrumb = forwardRef<HTMLDivElement, AppBreadcrumbProps>(
  ({ items, paramsLabel = '表示', showHome = true, showContainer = true, ...props }, ref) => {
    const params = useParams<{ site?: string; tenant?: string; id?: string }>();
    const site = params.site;
    const tenant = params.tenant;

    // Determine the correct home path. Admin routes match :site, tenant routes match :tenant.
    const basePath = site ? `/${site}/admin` : `/${tenant ?? 'medix'}`;

    const HOME_ITEM = {
      title: 'Home',
      href: basePath,
    };

    const mergedItems = useMemo(() => {
      if (!items || items.length === 0) return showHome ? [HOME_ITEM] : [];
      if (items[0]?.title === 'Home') return items;
      return showHome ? [HOME_ITEM, ...items] : items;
    }, [items, showHome]);

    const styledItems = useMemo(() => {
      const lastIndex = mergedItems.length - 1;

      return mergedItems.map((item, index) => {
        let title = item.title;

        if (typeof title === 'string' && title.startsWith(':')) {
          const id = params.id;
          title = id ? `${paramsLabel}:${id}` : title;
        }

        return {
          ...item,
          title: (
            <span
              style={{
                color: index === lastIndex ? 'rgba(0, 0, 0, 0.88)' : '#1890ff',
                cursor: index === lastIndex ? 'default' : 'pointer',
              }}
            >
              {title}
            </span>
          ),
        };
      });
    }, [mergedItems, params]);

    return (
      <div ref={ref} className={`${showContainer ? 'px-4 py-3 bg-gray-100 rounded' : ''}`}>
        <Breadcrumb {...props} items={styledItems} />
      </div>
    );
  }
);

AppBreadcrumb.displayName = 'AppBreadcrumb';

export default AppBreadcrumb;
